import type { PrivateAsset } from '@nest-react/domain';

import { ApiRequestError } from '~/app/regionalApiClient';
import {
  deletePrivateAsset,
  listPrivateAssets,
  privateAssetUrl,
  setPrivateAssetFavorite,
  uploadPrivateAsset,
} from '~/repositories/assetRepository';
import {
  classifyAssetFile,
  isSupportedAsset,
} from '~/features/assets/assetMedia';
import {
  type AssetUploadItem,
  createUploadItems,
  removeUploadItem,
  updateUploadItem,
} from '~/features/assets/uploadQueue';

import type { AssetMutationError, WorkspaceAssetsSnapshot } from './types';

type PendingUpload = {
  id: string;
  workspaceId: string;
  generation: number;
  resolve: (asset: PrivateAsset | null) => void;
};

type PatchSnapshot = (
  workspaceId: string,
  patch: Partial<WorkspaceAssetsSnapshot>
) => void;

const uploadErrorMessage = (reason: unknown): string =>
  reason instanceof Error ? reason.message : 'Upload failed';

const SIGNED_URL_REFRESH_MS = 14 * 60 * 1000;

const deleteErrorCode = (reason: unknown): AssetMutationError => {
  if (!(reason instanceof ApiRequestError)) return 'deleteFailed';
  if (reason.status === 403) return 'deleteForbidden';
  if (reason.status === 404) return 'deleteMissing';
  if (reason.status === 409) return 'deleteInUse';
  return 'deleteFailed';
};

class WorkspaceAssetsRuntime {
  readonly workspaceId: string;

  private readonly patchSnapshot: PatchSnapshot;

  private refCount = 0;

  private alive = true;

  private uploads: AssetUploadItem[] = [];

  private pending: PendingUpload[] = [];

  private runningGenerations = new Set<number>();

  private activePending = new Map<number, PendingUpload>();

  private generation = 0;

  private activeAbort: AbortController | null = null;

  private assets: PrivateAsset[] = [];

  private urls: Record<string, string> = {};

  private mutationVersion = 0;

  private additionVersions = new Map<string, number>();

  private deletionVersions = new Map<string, number>();

  private requestSequence = 0;

  private nextCursor: string | null = null;

  private pageCount = 1;

  private paginationVersion = 0;

  private loadMoreRequest: symbol | null = null;

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  private completionTimers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(workspaceId: string, patchSnapshot: PatchSnapshot) {
    this.workspaceId = workspaceId;
    this.patchSnapshot = patchSnapshot;
  }

  retain(): () => void {
    this.refCount += 1;
    if (this.refCount === 1) {
      this.alive = true;
      if (this.assets.length === 0) {
        this.patch({ loading: true, error: null });
      }
      void this.load();
    }
    return () => this.release();
  }

  private release(): void {
    this.refCount = Math.max(0, this.refCount - 1);
    if (this.refCount > 0) return;
    this.alive = false;
    this.generation += 1;
    this.requestSequence += 1;
    this.clearRefreshTimer();
    this.activeAbort?.abort();
    this.activeAbort = null;
    for (const pending of this.pending.splice(0)) {
      pending.resolve(null);
    }
    for (const pending of this.activePending.values()) {
      pending.resolve(null);
    }
    this.activePending.clear();
    for (const timer of this.completionTimers.values()) {
      clearTimeout(timer);
    }
    this.completionTimers.clear();
  }

  private patch(patch: Partial<WorkspaceAssetsSnapshot>): void {
    this.patchSnapshot(this.workspaceId, patch);
  }

  private replaceUploads(
    updater: (current: AssetUploadItem[]) => AssetUploadItem[]
  ): void {
    this.uploads = updater(this.uploads);
    this.patch({ uploads: this.uploads });
  }

  private replaceAssetState(
    nextAssets: PrivateAsset[],
    nextUrls: Record<string, string>
  ): void {
    this.assets = nextAssets;
    this.urls = nextUrls;
    this.patch({ assets: nextAssets, urls: nextUrls });
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  load = async (background = false): Promise<void> => {
    if (!this.alive) return;
    const requestSequence = ++this.requestSequence;
    const mutationSnapshot = this.mutationVersion;
    const paginationVersion = this.paginationVersion;
    this.clearRefreshTimer();
    if (!background) {
      this.patch({ loading: true, error: null });
    }
    try {
      const pages = [];
      let cursor: string | undefined;
      const targetPages = background ? this.pageCount : 1;
      for (let index = 0; index < targetPages; index += 1) {
        const response = await listPrivateAssets(
          this.workspaceId,
          false,
          cursor
        );
        const page = Array.isArray(response)
          ? { items: response as PrivateAsset[], nextCursor: null }
          : response;
        pages.push(...page.items);
        cursor = page.nextCursor || undefined;
        if (!cursor) break;
      }
      const next = pages.filter(isSupportedAsset);
      if (
        !this.alive ||
        this.requestSequence !== requestSequence ||
        this.paginationVersion !== paginationVersion
      ) {
        return;
      }
      const listedIds = new Set(next.map(asset => asset.id));
      const preserved = this.assets.filter(asset => {
        const addedAt = this.additionVersions.get(asset.id);
        const deletedAt = this.deletionVersions.get(asset.id);
        return (
          !listedIds.has(asset.id) &&
          addedAt !== undefined &&
          addedAt > mutationSnapshot &&
          !(deletedAt !== undefined && deletedAt > mutationSnapshot)
        );
      });
      const serverAssets = next.filter(asset => {
        const deletedAt = this.deletionVersions.get(asset.id);
        return deletedAt === undefined || deletedAt <= mutationSnapshot;
      });
      const nextAssets = [...preserved, ...serverAssets];
      const serverUrls = Object.fromEntries(
        serverAssets.map(asset => [asset.id, asset.url ?? ''])
      );
      const nextUrls = Object.fromEntries(
        nextAssets.map(asset => [
          asset.id,
          serverUrls[asset.id] ?? this.urls[asset.id] ?? '',
        ])
      );
      this.replaceAssetState(nextAssets, nextUrls);
      this.nextCursor = cursor ?? null;
      this.patch({ error: null, nextCursor: this.nextCursor });
      for (const [assetId, version] of this.additionVersions) {
        if (version <= mutationSnapshot || listedIds.has(assetId)) {
          this.additionVersions.delete(assetId);
        }
      }
      for (const [assetId, version] of this.deletionVersions) {
        if (version <= mutationSnapshot) {
          this.deletionVersions.delete(assetId);
        }
      }
    } catch {
      if (this.alive && this.requestSequence === requestSequence) {
        if (!background || this.assets.length === 0) {
          this.patch({ error: 'Assets could not be loaded.' });
        }
      }
    } finally {
      if (this.alive && this.requestSequence === requestSequence) {
        if (!background) {
          this.patch({ loading: false });
        }
        this.refreshTimer = setTimeout(() => {
          this.refreshTimer = null;
          void this.load(true);
        }, SIGNED_URL_REFRESH_MS);
      }
    }
  };

  private isCurrentUpload(
    pending: Pick<PendingUpload, 'workspaceId' | 'generation'>
  ): boolean {
    return (
      this.alive &&
      this.workspaceId === pending.workspaceId &&
      this.generation === pending.generation
    );
  }

  private processUpload = async (
    pending: PendingUpload
  ): Promise<PrivateAsset | null> => {
    if (!this.isCurrentUpload(pending)) return null;
    const item = this.uploads.find(upload => upload.id === pending.id);
    if (!item) return null;
    const classification = classifyAssetFile(item.file);
    if (!classification) return null;

    const controller = new AbortController();
    this.activeAbort = controller;
    this.replaceUploads(current =>
      updateUploadItem(current, pending.id, {
        status: 'preparing',
        progress: 0,
        error: undefined,
      })
    );
    try {
      const asset = await uploadPrivateAsset(
        pending.workspaceId,
        item.file,
        classification.type,
        classification.role,
        {
          signal: controller.signal,
          onHashProgress: value => {
            if (!this.isCurrentUpload(pending)) return;
            this.replaceUploads(current =>
              updateUploadItem(current, pending.id, {
                status: 'preparing',
                progress: value,
              })
            );
          },
          onUploadProgress: value => {
            if (!this.isCurrentUpload(pending)) return;
            this.replaceUploads(current =>
              updateUploadItem(current, pending.id, {
                status: 'uploading',
                progress: value,
              })
            );
          },
        }
      );
      if (!this.isCurrentUpload(pending)) return null;
      const mutationVersion = ++this.mutationVersion;
      this.additionVersions.set(asset.id, mutationVersion);
      const nextAssets = [
        asset,
        ...this.assets.filter(existing => existing.id !== asset.id),
      ];
      this.assets = nextAssets;
      this.patch({ assets: nextAssets });
      let url = '';
      try {
        url = await privateAssetUrl(pending.workspaceId, asset.id);
      } catch {
        url = '';
      }
      if (!this.isCurrentUpload(pending)) return null;
      const nextUrls = { ...this.urls, [asset.id]: url };
      this.urls = nextUrls;
      this.patch({ urls: nextUrls });
      this.replaceUploads(current =>
        updateUploadItem(current, pending.id, {
          status: 'complete',
          progress: 1,
          error: undefined,
        })
      );
      const timer = setTimeout(() => {
        if (!this.isCurrentUpload(pending)) return;
        this.replaceUploads(current => removeUploadItem(current, pending.id));
        this.completionTimers.delete(pending.id);
      }, 3000);
      this.completionTimers.set(pending.id, timer);
      return asset;
    } catch (reason) {
      if (!this.isCurrentUpload(pending)) return null;
      this.replaceUploads(current =>
        updateUploadItem(current, pending.id, {
          status: 'failed',
          error: uploadErrorMessage(reason),
        })
      );
      return null;
    } finally {
      if (this.activeAbort === controller) {
        this.activeAbort = null;
      }
    }
  };

  private runWorker = async (targetGeneration: number): Promise<void> => {
    if (this.runningGenerations.has(targetGeneration)) return;
    this.runningGenerations.add(targetGeneration);
    try {
      while (true) {
        const pendingIndex = this.pending.findIndex(
          pending => pending.generation === targetGeneration
        );
        if (pendingIndex < 0) break;
        const [nextPending] = this.pending.splice(pendingIndex, 1);
        if (!nextPending) break;
        this.activePending.set(targetGeneration, nextPending);
        const asset = await this.processUpload(nextPending);
        nextPending.resolve(asset);
        if (this.activePending.get(targetGeneration) === nextPending) {
          this.activePending.delete(targetGeneration);
        }
      }
    } finally {
      this.runningGenerations.delete(targetGeneration);
      if (
        this.pending.some(pending => pending.generation === targetGeneration)
      ) {
        void this.runWorker(targetGeneration);
      }
    }
  };

  private enqueueUploads = (
    ids: string[],
    targetWorkspaceId: string
  ): Promise<PrivateAsset[]> => {
    const targetGeneration = this.generation;
    const completions = ids.map(
      id =>
        new Promise<PrivateAsset | null>(resolve => {
          this.pending.push({
            id,
            workspaceId: targetWorkspaceId,
            generation: targetGeneration,
            resolve,
          });
        })
    );
    void this.runWorker(targetGeneration);
    return Promise.all(completions).then(completedAssets =>
      completedAssets.filter((asset): asset is PrivateAsset => asset !== null)
    );
  };

  uploadFiles = (files: File[]): Promise<PrivateAsset[]> => {
    if (files.length === 0) return Promise.resolve([]);
    const classified = files.map(file => {
      const classification = classifyAssetFile(file);
      return {
        file,
        mediaType: classification?.type ?? ('DOCUMENT' as PrivateAsset['type']),
        supported: Boolean(classification),
      };
    });
    const nextItems = createUploadItems(classified);
    const unsupportedFiles = new Set(
      classified.filter(item => !item.supported).map(item => item.file)
    );
    const visibleItems = nextItems.map(item =>
      unsupportedFiles.has(item.file)
        ? {
            ...item,
            status: 'failed' as const,
            error: 'Unsupported file type',
          }
        : item
    );
    this.replaceUploads(current => [...current, ...visibleItems]);
    return this.enqueueUploads(
      visibleItems
        .filter(item => item.status === 'queued')
        .map(item => item.id),
      this.workspaceId
    );
  };

  retryUpload = (id: string): Promise<void> => {
    const item = this.uploads.find(upload => upload.id === id);
    if (!item || !classifyAssetFile(item.file)) return Promise.resolve();
    const timer = this.completionTimers.get(id);
    if (timer) clearTimeout(timer);
    this.completionTimers.delete(id);
    this.replaceUploads(current =>
      updateUploadItem(current, id, {
        status: 'queued',
        progress: 0,
        error: undefined,
      })
    );
    return this.enqueueUploads([id], this.workspaceId).then(() => undefined);
  };

  dismissUpload = (id: string): void => {
    const timer = this.completionTimers.get(id);
    if (timer) clearTimeout(timer);
    this.completionTimers.delete(id);
    this.replaceUploads(current => removeUploadItem(current, id));
  };

  remove = async (asset: PrivateAsset): Promise<void> => {
    const targetGeneration = this.generation;
    this.patch({ mutationError: null });
    try {
      await deletePrivateAsset(this.workspaceId, asset.id);
      if (!this.alive || this.generation !== targetGeneration) {
        return;
      }
      const mutationVersion = ++this.mutationVersion;
      this.deletionVersions.set(asset.id, mutationVersion);
      this.additionVersions.delete(asset.id);
      const nextAssets = this.assets.filter(item => item.id !== asset.id);
      const nextUrls = { ...this.urls };
      delete nextUrls[asset.id];
      this.replaceAssetState(nextAssets, nextUrls);
    } catch (error) {
      if (this.alive && this.generation === targetGeneration) {
        this.patch({ mutationError: deleteErrorCode(error) });
      }
    }
  };

  setFavorite = async (
    asset: PrivateAsset,
    favorite: boolean
  ): Promise<void> => {
    this.patch({ mutationError: null });
    try {
      await setPrivateAssetFavorite(this.workspaceId, asset.id, favorite);
      const nextAssets = this.assets.map(item =>
        item.id === asset.id ? { ...item, favorite } : item
      );
      this.replaceAssetState(nextAssets, this.urls);
    } catch {
      this.patch({ mutationError: 'favoriteFailed' });
    }
  };

  loadMore = async (): Promise<void> => {
    if (this.loadMoreRequest || !this.nextCursor) return;
    const targetCursor = this.nextCursor;
    const targetGeneration = this.generation;
    const request = Symbol('asset-load-more');
    this.patch({ loadingMore: true, loadMoreError: null });
    this.loadMoreRequest = request;
    try {
      const response = await listPrivateAssets(
        this.workspaceId,
        false,
        targetCursor
      );
      const page = Array.isArray(response)
        ? { items: response as PrivateAsset[], nextCursor: null }
        : response;
      if (!this.alive || this.generation !== targetGeneration) return;
      const seen = new Set(this.assets.map(asset => asset.id));
      const additions = page.items.filter(
        asset => isSupportedAsset(asset) && !seen.has(asset.id)
      );
      const nextAssets = [...this.assets, ...additions];
      const nextUrls = {
        ...this.urls,
        ...Object.fromEntries(
          additions.map(asset => [asset.id, asset.url ?? ''])
        ),
      };
      this.paginationVersion += 1;
      this.replaceAssetState(nextAssets, nextUrls);
      this.nextCursor = page.nextCursor;
      this.pageCount += 1;
      this.patch({
        nextCursor: page.nextCursor,
      });
    } catch {
      if (this.alive && this.generation === targetGeneration) {
        this.patch({ loadMoreError: 'More assets could not be loaded.' });
      }
    } finally {
      if (this.loadMoreRequest === request) {
        if (this.alive && this.generation === targetGeneration) {
          this.patch({ loadingMore: false });
        }
        this.loadMoreRequest = null;
      }
    }
  };
}

const runtimes = new Map<string, WorkspaceAssetsRuntime>();

export const getWorkspaceAssetsRuntime = (
  workspaceId: string,
  patchSnapshot: PatchSnapshot
): WorkspaceAssetsRuntime => {
  const existing = runtimes.get(workspaceId);
  if (existing) return existing;
  const runtime = new WorkspaceAssetsRuntime(workspaceId, patchSnapshot);
  runtimes.set(workspaceId, runtime);
  return runtime;
};

export type WorkspaceAssetsRuntimeController = Pick<
  WorkspaceAssetsRuntime,
  | 'load'
  | 'loadMore'
  | 'uploadFiles'
  | 'retryUpload'
  | 'dismissUpload'
  | 'remove'
  | 'setFavorite'
  | 'retain'
>;

export const getWorkspaceAssetsController = (
  workspaceId: string,
  patchSnapshot: PatchSnapshot
): WorkspaceAssetsRuntimeController =>
  getWorkspaceAssetsRuntime(workspaceId, patchSnapshot);

export const resetWorkspaceAssetsRuntimes = (): void => {
  runtimes.clear();
};
